import type { ApiClient } from '../client';

// What the agent may correct from the ficha. All three optional and all three read as «changed»:
// the caller sends a field only when the agent actually touched it, so an absent key means «leave
// it alone» and never «clear it». The phone is deliberately absent — it is the identity the panel
// resolves the «Usuario» by ([AS-002]) and the cache key that survives this write.
export interface UserUpdate {
  name?: string;
  email?: string;
  // The `id` of the address search — an integer, not the address text, and CONFIRMED by backend as
  // the very same `centros.id` that read returns ([AS-037] 🟢), which is what spares this feature a
  // second lookup. The ficha holds the address as TEXT, so with no explicit choice there is no id
  // to send and the key stays out.
  //
  // ⚠️ Writing it has an effect the panel does not send and cannot prevent: backend releases the
  // portal the «Usuario» had chosen (`location_address_id`, invisible here), so the refetched
  // `address` is the NEW BUILDING'S MAIN PORTAL — not necessarily the line the agent picked in the
  // search, which returns one row per portal. Same building, same matrícula, possibly a different
  // street line on screen. Re-saving the building an account is already in changes nothing, so it
  // never strips a portal the «Usuario» chose from the app.
  locationId?: number;
}

// The «Editar datos» WRITE: correct name, email and/or address of a «Usuario» in ONE call, never
// one per field. The id rides the path (url-encoded); the body carries only the fields that
// changed, so nothing the agent did not touch can be overwritten and no unasked-for PII reaches a
// request log on this route. The client attaches the Firebase token via the Authorization header.
// Empty 200 — nothing to parse; the caller refetches the account and the repainted ficha IS the
// effect, including the «Matrícula» backend reassigns when the location changes ([AS-023]).
//
// Shape CONFIRMED — [AS-055] 🟢, closed by backend on 2026-09-10 (CBX-11384, app-service ADR-0047).
// `PATCH` on the resource with no verb in the path: editing three fields is a partial update of the
// representation, unlike `…/delete` or `…/collect`, which carry a verb because they have nothing to
// patch. This is the panel's first and only route that patches a resource, which is why `ApiClient`
// grew `patch` for it. The write is ATOMIC (all or nothing) and at least one editable field must
// travel — an empty body answers 400, not a no-op 200.
export async function updateUser(
  client: ApiClient,
  id: string,
  changes: UserUpdate,
): Promise<void> {
  // Built key by key rather than spread-and-prune: a spread of the argument would carry whatever a
  // caller put in it, and `exactOptionalPropertyTypes` does not stop an `as never` at the call
  // site. This composes the wire shape from a closed list of three.
  const body: Record<string, string | number> = {};
  if (changes.name !== undefined) body.name = changes.name;
  if (changes.email !== undefined) body.email = changes.email;
  if (changes.locationId !== undefined) body.location_id = changes.locationId;

  await client.patch(`/app_service/panel/v1/users/${encodeURIComponent(id)}`, body);
}
